import type { RealEstateTab } from "components/_dashboard/contacts/table/core/ContactTableTabs";
import type { ContactTableRowComponent } from "components/_dashboard/contacts/table/rows/ContactTableDesktopRow";
import type { ReactNode } from "react";

export const ACTIVE_FIXED_COLUMNS = ["menu", "checkbox", "listingMatches"] as const;

export type ActiveFixedColumnsKey = typeof ACTIVE_FIXED_COLUMNS[number];

export type ActiveFixedColumns = Record<ActiveFixedColumnsKey, boolean>;

export const initialActiveFixedColumns: ActiveFixedColumns = {
  menu: true,
  checkbox: true,
  listingMatches: true
};

export type ContactActiveFixedColumns = {
  id: ActiveFixedColumnsKey;
  width?: number;
  children?: ReactNode;
  alwaysEnabled?: boolean;
};

type ContactTableFixedColumnsOptions = {
  components?: Partial<ContactTableRowComponent>;
  columnsToHide?: ActiveFixedColumnsKey[];
  hideStaticColumns?: boolean;
  activeFixedColumns?: ActiveFixedColumns;
};

type ContactTableFixedColumns = {
  parsedFixedActiveColumns: ContactActiveFixedColumns[];
  parsedFixedActiveColumnsKeys: ActiveFixedColumns;
};

export function getContactTableFixedColumns(
  currentTab: RealEstateTab,
  {
    components,
    columnsToHide,
    hideStaticColumns,
    activeFixedColumns
  }: ContactTableFixedColumnsOptions = {}
): ContactTableFixedColumns {
  const { menuButton, checkboxButton, listingMatchesComponent } = components ?? {};

  const rawFixedColumns: ContactActiveFixedColumns[] = [
    {
      id: "checkbox",
      width: 50,
      children: checkboxButton
    },
    {
      id: "menu",
      width: 40,
      children: menuButton,
      alwaysEnabled: true
    }
  ];

  let parsedFixedActiveColumns = rawFixedColumns;

  const shouldShowListingMatches = canShowListingMatches(currentTab);

  if (shouldShowListingMatches) {
    parsedFixedActiveColumns.push({
      id: "listingMatches",
      width: 100,
      children: listingMatchesComponent
    });
  }

  if (columnsToHide) {
    parsedFixedActiveColumns = parsedFixedActiveColumns.filter(
      ({ id }) => !columnsToHide.includes(id)
    );
  }

  if (activeFixedColumns) {
    parsedFixedActiveColumns = rawFixedColumns.filter(
      ({ id, alwaysEnabled }) => alwaysEnabled || activeFixedColumns[id]
    );
  }

  if (hideStaticColumns) {
    parsedFixedActiveColumns = parsedFixedActiveColumns.filter(
      ({ alwaysEnabled }) => !alwaysEnabled
    );
  }

  const parsedFixedActiveColumnsKeys = {} as ActiveFixedColumns;

  for (const columnKey of ACTIVE_FIXED_COLUMNS) {
    parsedFixedActiveColumnsKeys[columnKey] = parsedFixedActiveColumns.some(
      ({ id }) => id === columnKey
    );
  }

  return {
    parsedFixedActiveColumns,
    parsedFixedActiveColumnsKeys
  };
}

export function canShowListingMatches(currentTab: RealEstateTab): boolean {
  return ["hotCustomer", "buyer", "tenant", "all"].includes(currentTab);
}
